import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState, type MouseEvent, type PointerEvent, type ReactElement } from "react";
import { COMPASS_MAX_PIPS, type CompassPipBuffer } from "odyssey-shared";
import { Tooltip, logical } from "../../ui/kit/index.ts";
import { COMPASS_CANVAS, COMPASS_INSET, COMPASS_PIP_BOX } from "./compass.ts";
import { compassListText, createCompassList, pipWords, refreshedTipText } from "./compass-words.ts";

/**
 * The compass pips' interaction (P1.9-T190; ADR-061's amendment of 2026-10-14, item 11): a fixed pool of 16 transparent
 * buttons over the pixels the band's canvas draws, each the pip's 7 by 7 box (11 by 11 on a coarse pointer), the
 * higher pip winning where two overlap; hovering a pip names it in the kit's tooltip, one at a time; a mouse click
 * opens the Map at Region centred on the pip's target, through the Map button's own open; and a visually hidden list
 * reads every pip in draw order, written when its words change and at most once a second. No pip is a tab stop.
 */

/**
 * The tooltip's id.
 */
export const COMPASS_TIP_ID = "ot-minimap-compass-tip";

/**
 * How far a pip's tooltip stands from the pip's centre, in logical pixels: the pip's reach and a pixel.
 */
const TIP_GAP = 6;

/**
 * What the pips' owner can call.
 */
export interface CompassPipsHandle {
  readonly sync: () => void;
}

/**
 * The target tile a pip stands for, from the player's position.
 */
export interface CompassPipsProps {
  readonly buffer: CompassPipBuffer;
  readonly position: { readonly x: number; readonly y: number };
  readonly openAt: ((point: { readonly x: number; readonly y: number }) => void) | undefined;
}

/**
 * The tooltip showing: which slot it names, its words, and where it stands in the band.
 */
interface PipTip {
  readonly index: number;
  readonly text: string;
  readonly top: number | undefined;
  readonly bottom: number | undefined;
}

/**
 * The index of the pip a pointer event's target is, or -1.
 *
 * @param target The event's target.
 * @returns The slot's index.
 */
function pipIndexOf(target: EventTarget | null): number {
  const button = target instanceof Element ? target.closest<HTMLElement>("[data-compass-pip]") : null;
  return button === null ? -1 : Number(button.dataset["compassPip"]);
}

/**
 * The pip pool and the hidden list. The owner calls `sync` after the pip buffer is refilled; it moves only the buttons
 * whose pixel changed, hides the unused ones, and lets the list read the buffer if a second has passed.
 *
 * @param props The pip `buffer`, the player's `position` and the Map's `openAt`.
 * @param ref The handle the owner calls `sync` on.
 * @returns The buttons, the tooltip and the list.
 */
export const CompassPips = forwardRef<CompassPipsHandle, CompassPipsProps>(function CompassPips({ buffer, position, openAt }, ref): ReactElement {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const list = useRef<HTMLParagraphElement>(null);
  const drawn = useMemo(() => ({ x: new Int16Array(COMPASS_MAX_PIPS).fill(-1), y: new Int16Array(COMPASS_MAX_PIPS).fill(-1), shown: new Uint8Array(COMPASS_MAX_PIPS) }), []);
  const [tip, setTip] = useState<PipTip | null>(null);
  const tipIndex = useRef(-1);
  const tipText = useRef("");
  const writer = useMemo(
    () =>
      createCompassList({
        read: () => compassListText(buffer.slots, buffer.count),
        write: (text) => {
          if (list.current !== null) {
            list.current.textContent = text;
          }
        },
        now: () => performance.now(),
        later: (run, delay) => setTimeout(run, delay),
        cancel: (timer) => clearTimeout(timer as ReturnType<typeof setTimeout>),
      }),
    [buffer],
  );
  useEffect(() => () => writer.dispose(), [writer]);
  useImperativeHandle(
    ref,
    () => ({
      sync: (): void => {
        const reach = (COMPASS_PIP_BOX - 1) / 2;
        for (let at = 0; at < COMPASS_MAX_PIPS; at += 1) {
          const button = buttons.current[at];
          if (button === null || button === undefined) {
            continue;
          }
          if (at >= buffer.count) {
            if (drawn.shown[at] === 1) {
              drawn.shown[at] = 0;
              button.hidden = true;
            }
            continue;
          }
          const slot = buffer.slots[at];
          if (slot === undefined) {
            continue;
          }
          if (drawn.shown[at] === 0 || drawn.x[at] !== slot.x || drawn.y[at] !== slot.y) {
            drawn.shown[at] = 1;
            drawn.x[at] = slot.x;
            drawn.y[at] = slot.y;
            button.hidden = false;
            button.style.left = logical(slot.x - COMPASS_INSET - reach);
            button.style.top = logical(slot.y - COMPASS_INSET - reach);
          }
        }
        if (tipIndex.current >= buffer.count) {
          tipIndex.current = -1;
          setTip(null);
        } else if (tipIndex.current >= 0) {
          const text = refreshedTipText(tipText.current, buffer.slots, buffer.count, tipIndex.current);
          if (text !== null) {
            tipText.current = text;
            setTip((shown) => (shown === null ? null : { ...shown, text }));
          }
        }
        writer.touch();
      },
    }),
    [buffer, drawn, writer],
  );
  const handlers = useMemo(
    () => ({
      /**
       * Names the pip the pointer enters.
       *
       * @param event The pointer's move.
       */
      onPointerOver: (event: PointerEvent<HTMLElement>): void => {
        const index = pipIndexOf(event.target);
        const slot = index < 0 ? undefined : buffer.slots[index];
        if (slot === undefined || index >= buffer.count) {
          return;
        }
        const y = slot.y - COMPASS_INSET;
        const text = pipWords(slot);
        tipIndex.current = index;
        tipText.current = text;
        setTip({ index, text, top: y < COMPASS_CANVAS / 2 ? y + TIP_GAP : undefined, bottom: y < COMPASS_CANVAS / 2 ? undefined : COMPASS_CANVAS - y + TIP_GAP });
      },
      /**
       * Hides the tooltip when the pointer leaves the pip it names.
       *
       * @param event The pointer's move.
       */
      onPointerOut: (event: PointerEvent<HTMLElement>): void => {
        const index = pipIndexOf(event.target);
        if (index >= 0 && index === tipIndex.current) {
          tipIndex.current = -1;
          setTip(null);
        }
      },
      /**
       * Keeps a press on a pip from taking focus, so the game keeps its keys.
       *
       * @param event The press.
       */
      onMouseDown: (event: MouseEvent<HTMLElement>): void => event.preventDefault(),
      /**
       * Opens the Map on the pip's target when the mouse clicks it. A touch only names, so a stray tap never opens a window.
       *
       * @param event The click.
       */
      onClick: (event: MouseEvent<HTMLElement>): void => {
        const index = pipIndexOf(event.target);
        const slot = index < 0 ? undefined : buffer.slots[index];
        const pointerType = (event.nativeEvent as { pointerType?: string }).pointerType;
        if (slot === undefined || index >= buffer.count || pointerType === "touch" || openAt === undefined) {
          return;
        }
        tipIndex.current = -1;
        setTip(null);
        openAt({ x: position.x + slot.dx, y: position.y + slot.dy });
      },
    }),
    [buffer, position, openAt],
  );
  const pool: ReactElement[] = [];
  for (let at = 0; at < COMPASS_MAX_PIPS; at += 1) {
    pool.push(
      <button
        key={at}
        ref={(element) => {
          buttons.current[at] = element;
        }}
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        hidden
        className="ot-minimap__pip"
        data-compass-pip={at}
        style={{ zIndex: COMPASS_MAX_PIPS - at }}
      />,
    );
  }
  return (
    <>
      <div className="ot-minimap__pips" data-minimap="pips" {...handlers}>
        {pool}
      </div>
      {tip === null ? null : <Tooltip id={COMPASS_TIP_ID} text={tip.text} right={0} top={tip.top} bottom={tip.bottom} />}
      <p ref={list} className="ot-minimap__spoken" data-minimap="compass-list" />
    </>
  );
});
